import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  assertPreFlightScriptsAccess,
  resolvePreFlightScriptsAccess,
} from "@/features/auth/lib/assert-pre-flight-scripts-access";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { pageStateQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";

/**
 * Pre Flight – Scripts layout route: gated by the grantable SCRIPT_REVIEW_VIEW_PAGE
 * permission for the active tenant (same pattern as pre-flight / personas).
 * Production partners may enter linked review detail with marketplace.partner alone.
 */
export const Route = createFileRoute("/_authenticated/pre-flight-scripts")({
  beforeLoad: () => assertPreFlightScriptsAccess(),
  loader: ({ context }) => {
    const access = resolvePreFlightScriptsAccess(
      useAuth.getState().tenants,
      useAuth.getState().activeTenantId,
    );
    if (access !== "full") return null; // hub list only for the dedicated PAGE

    return context.queryClient
      .ensureQueryData(pageStateQueryOptions("pre-flight-scripts"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });
  },
  component: ScriptReviewLayout,
  errorComponent: PageAccessErrorComponent,
});

function ScriptReviewLayout() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.preFlightScripts", "Pre Flight – Scripts"));
  const access = resolvePreFlightScriptsAccess(
    useAuth((s) => s.tenants),
    useAuth((s) => s.activeTenantId),
  );
  const { isLoading, error } = usePageState("pre-flight-scripts");

  if (
    access === "partner-review" ||
    access === "marketplace-review" ||
    access === "ops-review"
  ) {
    return <Outlet />;
  }

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">Loading…</div>;
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <Outlet />;
}
